// @ts-nocheck

import React, { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../styled-components/dark';
import { type ObjectValuesUnionType } from '../../utils/typing';

export const InfosLabelTypes = {
  INFOS_GREY: 'INFOS_GREY',
  INFOS_BLUE: 'INFOS_BLUE',
  INFOS_YELLOW: 'INFOS_YELLOW',
  INFOS_RED: 'INFOS_RED',
  ALERT_YELLOW: 'ALERT_YELLOW',
  ALERT_RED: 'ALERT_RED',
};

type InfosLabelType = ObjectValuesUnionType<typeof InfosLabelTypes>;

const Wrapper = styled.div`
  position: relative;

  padding: 0.6em;
  margin: 0.5em 0;
  border-radius: 0.6em;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  ${({ $type }: { $type: InfosLabelType }) => {
    if ($type === InfosLabelTypes.INFOS_GREY) {
      return css`
        --InfosLabel-backgroundColor: ${theme.InfosLabel.infosGrey.backgroundColor};
        --InfosLabel-color: ${theme.InfosLabel.infosGrey.color};
        --InfosLabel-symbolBackgroundColor: ${theme.InfosLabel.infosGrey.symbolBackgroundColor};
      `;
    }

    if ($type === InfosLabelTypes.INFOS_BLUE) {
      return css`
        --InfosLabel-backgroundColor: ${theme.InfosLabel.infosBlue.backgroundColor};
        --InfosLabel-color: ${theme.InfosLabel.infosBlue.color};
        --InfosLabel-symbolBackgroundColor: ${theme.InfosLabel.infosBlue.symbolBackgroundColor};
      `;
    }

    if ($type === InfosLabelTypes.INFOS_YELLOW) {
      return css`
        --InfosLabel-backgroundColor: ${theme.InfosLabel.infosYellow.backgroundColor};
        --InfosLabel-color: ${theme.InfosLabel.infosYellow.color};
        --InfosLabel-symbolBackgroundColor: ${theme.InfosLabel.infosYellow.symbolBackgroundColor};
      `;
    }

    if ($type === InfosLabelTypes.INFOS_RED) {
      return css`
        --InfosLabel-backgroundColor: ${theme.InfosLabel.infosRed.backgroundColor};
        --InfosLabel-color: ${theme.InfosLabel.infosRed.color};
        --InfosLabel-symbolBackgroundColor: ${theme.InfosLabel.infosRed.symbolBackgroundColor};
      `;
    }

    return css`
      --InfosLabel-backgroundColor: ${theme.InfosLabel.infosGrey.backgroundColor};
      --InfosLabel-color: ${theme.InfosLabel.infosGrey.color};
      --InfosLabel-symbolBackgroundColor: ${theme.InfosLabel.infosGrey.symbolBackgroundColor};
    `;
  }}

  background-color: var(--InfosLabel-backgroundColor);
`;

const INFOS_ICON_PADDING = 0.625; // em

const Icon = styled.div`
  position: relative;
  padding: ${INFOS_ICON_PADDING / 3}em ${INFOS_ICON_PADDING}em;
  color: var(--InfosLabel-backgroundColor);
  transform-style: preserve-3d;
  font-weight: bold;

  margin-right: 0.5em;

  &:after {
    content: '';
    position: absolute;
    width: ${INFOS_ICON_PADDING * 2}em;
    height: ${INFOS_ICON_PADDING * 2}em;
    left: 50%;
    top: 50%;
    transform: translate3d(-50%, -50%, -1px);
    z-index: -1;
    border-radius: 50%;
    background-color: var(--InfosLabel-symbolBackgroundColor);
  }
`;

const Label = styled.div`
  width: 100%;
  color: var(--InfosLabel-color);
`;

type InfosLabelProps = {
  className?: string;
  symbol?: string;
  label?: string;
  type?: InfosLabelType;
};

//$FlowFixMe
const InfosLabel = forwardRef(function InfosLabel(
  { className = '', symbol, label, type = InfosLabelTypes.INFOS_GREY }: InfosLabelProps,
  ref,
) {
  const wrapperNodeRef = useRef();
  const labelNodeRef = useRef();

  const recomputeAlignement = useCallback(() => {
    if (label?.length) {
      const labelNode = labelNodeRef.current;
      const wrapperNode = wrapperNodeRef.current;

      if (labelNode && wrapperNode) {
        const labelComputedStyle = window.getComputedStyle(labelNode);

        if (labelComputedStyle) {
          const width = parseInt(labelComputedStyle.width, 10);
          const fontSize = parseInt(labelComputedStyle.fontSize, 10);
          const fontSizeWidthRatio = 0.367;

          const nbLettersMultilineThreshold = width / (fontSize * fontSizeWidthRatio);

          if ((label?.length ?? 0) >= Math.floor(nbLettersMultilineThreshold)) {
            wrapperNode.style.alignItems = 'flex-start';
          } else {
            wrapperNode.style.alignItems = 'center';
          }
        }
      }
    }
  }, [label]);

  useLayoutEffect(() => {
    recomputeAlignement();

    window.removeEventListener('resize', recomputeAlignement);
    window.addEventListener('resize', recomputeAlignement);

    return () => {
      window.removeEventListener('resize', recomputeAlignement);
    };
  }, [recomputeAlignement]);

  return (
    <Wrapper
      ref={(thisRef) => {
        wrapperNodeRef.current = thisRef;

        if (typeof ref === 'function') {
          ref(thisRef);
        }
      }}
      className={className}
      $type={type}
    >
      {symbol ? <Icon $hasLabel={!!label}>{symbol}</Icon> : null}
      {label ? <Label ref={labelNodeRef} dangerouslySetInnerHTML={{ __html: label }} /> : null}
    </Wrapper>
  );
});

export default InfosLabel;
